import { useState, useEffect, useLayoutEffect, useRef, Fragment } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getOrgTimezone, formatDateTime } from "../lib/timezone";
import { getEffectiveMembershipStatus } from "../lib/utils";
import { MembershipModal } from "../components/MembershipModal";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { ClickableAvatar } from "../components/ClickableAvatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { AddPaymentModal } from "../components/AddPaymentModal";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { toast } from "sonner";
import { Download, CreditCard, Eye, Search, Filter, X, ChevronDown, Trash2, Clock, ChevronLeft, ChevronRight, Package, Wallet, CalendarDays, Plus, Phone, ArrowRight } from "lucide-react";
import { WhatsApp } from "../components/ui/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function useIsDesktop() {
  const mq = typeof window !== "undefined"
    ? window.matchMedia("(min-width: 640px)")
    : null;
  const [isDesktop, setIsDesktop] = useState(mq ? mq.matches : true);
  useLayoutEffect(() => {
    if (!mq) return;
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function Memberships() {
  const { currentBranch, organization, hasPermission } = useAuth();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const orgTimezone = getOrgTimezone(organization);

  const canAddPayment = hasPermission("add_payment");
  const canDeleteMembership = hasPermission("delete_membership");
  const canExportMemberships = hasPermission("export_memberships");
  const canRenew = hasPermission("create_membership");

  const [memberships, setMemberships] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMembership, setPaymentMembership] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ status: "", packageId: "", search: "" });
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, membership: null });
  const [renewMembership, setRenewMembership] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => { fetchPackages(); }, []);
  useEffect(() => { fetchData(); }, [page, limit, currentBranch, filters]);

  const fetchPackages = async () => {
    try { const res = await api.get("/packages"); setPackages(res.data.packages || []); } catch { }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (filters.status) params.append("status", filters.status);
      if (filters.search) params.append("name", filters.search);
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const res = await api.get(`/memberships?${params.toString()}`);
      let data = res.data.memberships;
      if (filters.packageId) data = data.filter((m) => m.packageId === filters.packageId);
      setMemberships(data);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const clearFilters = () => { setFilters({ status: "", packageId: "", search: "" }); setPage(1); };
  const toggleRow = (id) => setExpandedRowId(prev => (prev === id ? null : id));

  const handleDelete = async (id) => {
    setDeleteLoading(true);
    try {
      await api.delete(`/memberships/${id}`);
      toast.success("Membership deleted successfully");
      setDeleteModal({ open: false, membership: null });
      fetchData();
    } catch { toast.error("Failed to delete membership"); }
    finally { setDeleteLoading(false); }
  };

  const openPaymentModal = (ms) => { setPaymentMembership(ms); setShowPaymentModal(true); };

  const hasActiveFilters = filters.status || filters.packageId || filters.search;
  const activeFilterCount = [filters.status, filters.packageId, filters.search].filter(v =>
    Array.isArray(v) ? v.length > 0 : !!v
  ).length;

  const statusColor = (s) => {
    const map = {
      Active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
      Expired: "bg-red-500/15 text-red-500 border-red-500/20",
      Partial: "bg-amber-500/15 text-amber-500 border-amber-500/20",
      Pending: "bg-blue-500/15 text-blue-500 border-blue-500/20",
      Inactive: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
      Renewed: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
    };
    return map[s] || "bg-zinc-500/15 text-zinc-400 border-zinc-500/20";
  };

  const renderMembershipDetails = (ms) => {
    return (
      <div className="flex flex-col">
        {ms.member?.phone && (
          <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
            <a
              href={`tel:${ms.member.phone}`}
              onClick={(e) => e.stopPropagation()}
              className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
                <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                  Mobile Call
                </span>
                <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                  Call Now
                </span>
              </div>
            </a>
            <a
              href={`https://wa.me/${ms.member.phone}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
                <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                  Message
                </span>
                <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                  WhatsApp
                </span>
              </div>
            </a>
          </div>
        )}
        {/* Action Bar */}
        <div className="flex items-center justify-between gap-2 p-2 border-b border-border bg-secondary/20">
          <div className="text-xs text-muted-foreground font-mono hidden md:block">
            Membership ID: #{ms.id?.slice(-6).toUpperCase()}
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide"
              onClick={(e) => { e.stopPropagation(); navigate(`/memberships/${ms.id}`); }}
              data-testid={`view-ms-${ms.id}`}>
              <Eye className="h-3.5 w-3.5" /> <span className="hidden md:block">View Details</span>
            </Button>
            {canAddPayment && (
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                onClick={(e) => { e.stopPropagation(); openPaymentModal(ms); }}
                data-testid={`pay-ms-${ms.id}`}>
                <CreditCard className="h-3.5 w-3.5" /> <span className="hidden md:block">Add Payment</span>
              </Button>
            )}
            {/* Renew — shown when membership is expired */}
            {canRenew && getEffectiveMembershipStatus(ms, orgTimezone) === "Expired" && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-amber-500 hover:text-amber-600 hover:bg-amber-500/10 border-amber-500/30"
                onClick={(e) => { e.stopPropagation(); setRenewMembership(ms); }}
                data-testid={`renew-ms-${ms.id}`}
              >
                <ArrowRight className="h-3.5 w-3.5" /> <span className="hidden md:block">Renew</span>
              </Button>
            )}
            {canDeleteMembership && (
              <Button variant="ghost" size="sm" className="h-8 text-xs gap-1.5 font-heading uppercase tracking-wide text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto"
                onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, membership: ms }); }}
                data-testid={`delete-ms-${ms.id}`}>
                <Trash2 className="h-3.5 w-3.5" /> <span className="hidden md:block">Delete</span>
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
          {/* Column 1: Payment Summary */}
          <div className="lg:col-span-4 space-y-6 p-3">
            <div className="space-y-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-2">Payment Summary</p>
                <div className="space-y-2">
                  <div className="flex flex-col gap-2 bg-background/50 rounded-lg px-3 py-2 text-xs font-mono">
                    <div className="flex items-center gap-2">
                      <Wallet className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="font-sans font-medium">Total Amount:</span>
                      <span className="font-bold text-foreground">₹{(ms.finalAmount || ms.amount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-500">Paid: ₹{(ms.paidAmount || 0).toLocaleString()}</span>
                      {ms.remainingAmount > 0 && (
                        <span className="text-amber-500 font-bold">Due: ₹{ms.remainingAmount.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: Period & Package details */}
          <div className="lg:col-span-4 space-y-6 lg:border-l lg:border-border border-t md:border-t-0 p-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground flex items-center gap-1.5 mb-1">
                  <CalendarDays className="h-3 w-3" /> Start Date
                </p>
                <p className="text-sm font-normal font-mono">{ms.startDate ? ms.startDate.slice(0, 10) : "—"}</p>
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground flex items-center gap-1.5 mb-1">
                  <CalendarDays className="h-3 w-3" /> End Date
                </p>
                <p className="text-sm font-normal font-mono">{ms.endDate ? ms.endDate.slice(0, 10) : "—"}</p>
              </div>
              {ms.status === "Partial" && ms.partialPaymentDate && (
                <div className="col-span-2">
                  <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground flex items-center gap-1.5 mb-1">
                    <Clock className="h-3 w-3 text-amber-500" /> Next Payment Due
                  </p>
                  <p className="text-sm font-semibold text-amber-500 font-mono">
                    {ms.partialPaymentDate ? ms.partialPaymentDate.slice(0, 10) : "—"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Column 3: Last Payment Details */}
          <div className="lg:col-span-4 space-y-6 lg:border-l lg:border-border border-t md:border-t-0 p-3">
            <div>
              <p className="text-xs font-mono uppercase tracking-tight text-muted-foreground mb-4">Last Payment</p>
              {(() => {
                const last = ms.recentPayments?.[0] ?? ms.lastPayment ?? null;
                if (!last) return <p className="text-muted-foreground text-xs italic">No payment history</p>;
                return (
                  <div className="">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span>Paid: ₹{last.amount?.toLocaleString()}</span>
                        <span className="font-mono text-muted-foreground">
                          {formatDateTime(last.date, orgTimezone)}
                        </span>
                      </div>
                      {last.paymentMode && <Badge variant="outline" className="text-[10px] font-mono">{last.paymentMode}</Badge>}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/memberships/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "memberships.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-2 mt-2 md:space-y-3" data-testid="memberships-page">
      {/* Header */}
      <div className="flex flex-row sm:items-center justify-between gap-2 sm:gap-4">
        <div>
          <h1 className="text-xl md:text-4xl font-black font-heading tracking-tight uppercase">Memberships</h1>
          <p className="text-xs tracking-tight text-muted-foreground mt-1 hidden md:block">Track all membership subscriptions</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={hasActiveFilters ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowFilters(true)}
            className="font-heading uppercase tracking-wide text-xs relative"
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />Filters
            {activeFilterCount > 0 && (
              <Badge className="ml-1.5 bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px] leading-none flex items-center justify-center">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          {canExportMemberships && (
            <Button variant="outline" size="sm" onClick={handleExport}
              className="font-heading uppercase tracking-wide text-xs" data-testid="export-memberships-btn">
              <Download className="h-4 w-4 mr-2" />Export
            </Button>
          )}
        </div>
      </div>

      {/* ── Filter Drawer – bottom on mobile, right on desktop ── */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className={isDesktop
            ? "w-80 flex flex-col gap-0 p-0"
            : "flex flex-col gap-0 p-0 rounded-t-2xl max-h-[92vh]"}
        >
          <SheetHeader className="px-6 py-5 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground rounded-full text-[10px] h-5 px-1.5">
                    {activeFilterCount} active
                  </Badge>
                )}
              </SheetTitle>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Clear all
                </button>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Search Member</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Member name..."
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  className="pl-9"
                  data-testid="filter-search"
                />
              </div>
            </div>

            <div className={`grid gap-4 ${!isDesktop ? "grid-cols-2" : "grid-cols-1"}`}>
              <div className="space-y-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</Label>
                <Select
                  value={filters.status || "all"}
                  onValueChange={(v) => { setFilters(f => ({ ...f, status: v === "all" ? "" : v })); setPage(1); }}
                >
                  <SelectTrigger data-testid="filter-ms-status"><SelectValue placeholder="All Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Partial">Partial</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Package</Label>
                <Select
                  value={filters.packageId || "all"}
                  onValueChange={(v) => { setFilters(f => ({ ...f, packageId: v === "all" ? "" : v })); setPage(1); }}
                >
                  <SelectTrigger data-testid="filter-ms-package"><SelectValue placeholder="All Packages" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Packages</SelectItem>
                    {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-border shrink-0">
            <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={() => setShowFilters(false)}>
              Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── List / Table Card ── */}
      <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0 md:mx-0 rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            <span className="text-muted-foreground text-sm">Loading...</span>
          </div>
        ) : memberships.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
            No memberships found
          </div>
        ) : (
          <>
            {/* ── Mobile card list (hidden md+) ── */}
            <div className="md:hidden overflow-y-auto flex-1">
              {memberships.map((ms, idx) => (
                <Collapsible
                  key={ms.id}
                  open={expandedRowId === ms.id}
                  onOpenChange={() => toggleRow(ms.id)}
                  asChild
                >
                  <div data-testid={`membership-card-${ms.id}`}
                    className={`border-b border-border hover:bg-secondary/10 transition-colors`}>

                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-2 py-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="flex items-center justify-between flex-col">
                            <ClickableAvatar
                              src={ms.member?.photo}
                              name={ms.member?.name}
                              className="h-9 w-9 border border-border"
                              fallbackClassName="bg-primary/10 text-primary text-xs font-heading font-bold"
                            />
                            <Button variant="ghost" size="icon" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0">
                              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedRowId === ms.id ? "rotate-180" : ""}`} />
                            </Button>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-semibold text-sm leading-tight truncate">{ms.member?.name}</p>
                              <span className="text-[10px] font-mono text-muted-foreground shrink-0">#{ms.id?.slice(-6).toUpperCase()}</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{ms.member?.phone}</p>
                            <div className="flex items-center gap-1.5 mt-1 text-[11px] font-mono text-muted-foreground">
                              <Package className="h-3 w-3 shrink-0 text-primary/70" />
                              <span className="truncate max-w-[120px]">{ms.packageName}</span>
                            </div>
                            {ms.renewedFromPackageName && (
                              <p className="text-[10px] text-muted-foreground font-mono mt-0.5 pl-4.5">
                                🔄 Renewed from: {ms.renewedFromPackageName}
                              </p>
                            )}
                            {ms.renewedToPackageName && (
                              <p className="text-[10px] text-muted-foreground font-mono mt-0.5 pl-4.5">
                                ➡️ Renewed to: {ms.renewedToPackageName}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex items-end flex-col gap-0.5 text-right">
                            <Badge className={`${statusColor(getEffectiveMembershipStatus(ms, orgTimezone))} border text-[9px] font-mono uppercase py-0 px-1.5`}>
                              {getEffectiveMembershipStatus(ms, orgTimezone)}
                            </Badge>
                            {getEffectiveMembershipStatus(ms, orgTimezone) !== "Partial" && ms.endDate && (
                              <span className="text-[9px] font-mono text-muted-foreground">
                                {ms.endDate?.slice(0, 10)}
                              </span>
                            )}
                            {getEffectiveMembershipStatus(ms, orgTimezone) === "Partial" && ms.partialPaymentDate && (
                              <span className="text-[9px] font-mono text-muted-foreground">
                                {ms.partialPaymentDate?.slice(0, 10)}
                              </span>
                            )}
                            <div className="mt-0.5 leading-none">
                              <p className="font-mono font-semibold text-[10px] text-foreground">₹{(ms.finalAmount || ms.amount || 0).toLocaleString()}</p>
                              {ms.remainingAmount > 0 && (
                                <p className="text-[9px] text-amber-500 font-mono mt-0.5">Due: ₹{ms.remainingAmount.toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className=" border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
                        {renderMembershipDetails(ms)}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>

            {/* ── Desktop table (hidden below md) ── */}
            <div className="hidden md:block overflow-auto flex-1">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Member</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memberships.map((ms) => (
                    <Fragment key={ms.id}>
                      <TableRow
                        className="group cursor-pointer hover:bg-secondary/50"
                        data-testid={`membership-row-${ms.id}`}
                        onClick={() => toggleRow(ms.id)}
                      >
                        <TableCell className="w-10">
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <ChevronDown className={`h-4 w-4 transition-transform ${expandedRowId === ms.id ? "rotate-180" : ""}`} />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <ClickableAvatar
                              src={ms.member?.photo}
                              name={ms.member?.name}
                              className="h-9 w-9 border border-border"
                              fallbackClassName="bg-primary/10 text-primary text-xs font-heading"
                            />
                            <div>
                              <p className="font-medium text-sm">{ms.member?.name}</p>
                              <p className="text-[10px] text-muted-foreground font-mono">ID: {ms.id?.slice(-6).toUpperCase()}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{ms.member?.phone || "—"}</TableCell>
                        <TableCell className="text-sm">
                          <div>
                            <p>{ms.packageName}</p>
                            {ms.renewedFromPackageName && (
                              <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                🔄 Renewed from: {ms.renewedFromPackageName}
                              </p>
                            )}
                            {ms.renewedToPackageName && (
                              <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                ➡️ Renewed to: {ms.renewedToPackageName}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-mono">{ms.startDate?.slice(0, 10)}</p>
                            <p className="text-[10px] text-muted-foreground">to {ms.endDate?.slice(0, 10)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-mono font-medium">₹{(ms.finalAmount || ms.amount || 0).toLocaleString()}</p>
                            {ms.remainingAmount > 0 && (
                              <p className="text-[10px] text-amber-500 font-mono">Due: ₹{ms.remainingAmount.toLocaleString()}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-start flex-col gap-0.5">
                            <Badge className={`${statusColor(getEffectiveMembershipStatus(ms, orgTimezone))} border text-[10px] font-mono uppercase py-0 `}>
                              {getEffectiveMembershipStatus(ms, orgTimezone)}
                            </Badge>
                            {getEffectiveMembershipStatus(ms, orgTimezone) === "Partial" && ms.partialPaymentDate && (
                              <span className="text-[10px] font-mono text-muted-foreground">
                                {ms.partialPaymentDate?.slice(0, 10)}
                              </span>
                            )}
                            {getEffectiveMembershipStatus(ms, orgTimezone) !== "Partial" && ms.endDate && (
                              <span className="text-[10px] font-mono text-muted-foreground">
                                {ms.endDate?.slice(0, 10)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>

                      {expandedRowId === ms.id && (
                        <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                          <TableCell colSpan={7} className="p-0">
                            {renderMembershipDetails(ms)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && memberships.length > 0 && (
          <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="font-mono hidden sm:block">{total} total</span>
              <div className="flex items-center gap-2">
                <span className="text-xs">Show:</span>
                <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft /></Button>
              <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {page} of {Math.ceil(total / limit) || 1}</span>
              <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(page + 1)}><ChevronRight /></Button>
            </div>
          </div>
        )}
      </Card>

      <AddPaymentModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        membership={paymentMembership}
        packageName={paymentMembership?.packageName || ""}
        onSuccess={fetchData}
      />

      <DeleteConfirmModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal({ open, membership: open ? deleteModal.membership : null })}
        title="Delete Membership"
        description="Are you sure you want to delete this membership? This action cannot be undone."
        itemName={`${deleteModal.membership?.member?.name} - ${deleteModal.membership?.packageName}`}
        onConfirm={() => handleDelete(deleteModal.membership?.id)}
        loading={deleteLoading}
      />

      <MembershipModal
        open={!!renewMembership}
        onOpenChange={(o) => { if (!o) setRenewMembership(null); }}
        member={renewMembership?.member}
        membership={renewMembership}
        isRenewal={true}
        packages={packages.filter(p => p.status === "Active")}
        branchId={currentBranch?.id}
        onSuccess={fetchData}
      />
    </div>
  );
}
